import { html } from 'lit';
import { live } from 'lit/directives/live.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { ref } from 'lit/directives/ref.js';

import { map } from 'lit/directives/map.js';
import examples from 'libphonenumber-js/mobile/examples';
import { getExampleNumber, getCountryCallingCode } from 'libphonenumber-js';
import { countryNames } from '../utilities/countries';
import { FloatField } from './float_field';
import {
  requiredNumberValidator,
  requiredValidator,
} from '../utilities/validators';
class FloatPhoneField extends FloatField {
  static get properties() {
    return {
      countries: { attribute: false },
      currentCountry: { type: Object, state: true },
      open: { type: Boolean, state: true },
      countryWidth: { type: Number, state: true },
      intPhone: { type: String, attribute: true, reflect: true },
      isoCodes: {
        type: Array,
        converter: {
          fromAttribute: (value) => (value ? value.split(',') : []),
          toAttribute: (value) => value.join(','),
        },
      },
      countryCode: { type: String, attribute: true, reflect: true },
    };
  }

  constructor() {
    super();
    this.countries = [];

    this.currentCountry = null;
    this.validators = [
      {
        ...requiredValidator,
        isValid: () =>
          this.isStringType
            ? requiredValidator.isValid(this)
            : requiredNumberValidator.isValid(this),
      },
    ];
  }

  get intPhone() {
    const currentCallingCode = this.currentCountry.callingCode;
    const prefix = `+${currentCallingCode}`;

    if (!this.value) {
      return '';
    }

    if (this.value.startsWith(prefix)) {
      return this.value;
    } else {
      return `${prefix}${this.value}`;
    }
  }

  set intPhone(value) {
    if (!value) {
      return '';
    }

    const currentCallingCode = this.currentCountry.callingCode;

    if (!currentCallingCode) {
      return;
    }

    const prefix = `+${currentCallingCode}`;

    if (value.startsWith(prefix)) {
      return value;
    } else {
      return `${prefix}${value}`;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleCountry();
    this.handleSwitchCountry(this.currentCountry, false);
    this.handleClickOutside();
  }

  firstUpdated() {
    if (this.isConnected) {
      setTimeout(() => {
        if (this.input && this.input?.value) {
          this.hasInteracted = true;
          this.value = this.input.value;
          this.intPhone = this.value;
        }
      }, 100);
    }
  }

  handleChange() {
    const value = this.input.value;
    this.value = value;
    this.intPhone = this.value;
    this.emitEvent('change', { detail: value });
  }

  handleCountry() {
    const isoCodes =
      this.isoCodes?.length > 0
        ? this.isoCodes
        : [
            'AU',
            'US',
            'GB',
            'CA',
            'NZ',
            'ZA',
            'IN',
            'CN',
            'JP',
            'KR',
            'AF',
            'AX',
            'AL',
            'DZ',
            'AS',
            'AD',
            'AO',
            'AI',
            'AG',
            'AR',
            'AM',
            'AW',
            'AU',
            'AT',
            'AZ',
            'BS',
            'BH',
            'BD',
            'BB',
            'BY',
            'BE',
            'BZ',
            'BJ',
            'BM',
            'BT',
            'BO',
            'BA',
            'BW',
            'BR',
            'IO',
            'VG',
            'BN',
            'BG',
            'BF',
            'BI',
            'KH',
            'CM',
            'CA',
            'CV',
            'BQ',
            'KY',
            'CF',
            'TD',
            'CL',
            'CN',
            'CX',
            'CC',
            'CO',
            'KM',
            'CG',
            'CD',
            'CK',
            'CR',
            'CI',
            'HR',
            'CU',
            'CW',
            'CY',
            'CZ',
            'DK',
            'DJ',
            'DM',
            'DO',
            'EC',
            'EG',
            'SV',
            'GQ',
            'ER',
            'EE',
            'SZ',
            'ET',
            'FK',
            'FO',
            'FJ',
            'FI',
            'FR',
            'GF',
            'PF',
            'GA',
            'GM',
            'GE',
            'DE',
            'GH',
            'GI',
            'GR',
            'GL',
            'GD',
            'GP',
            'GU',
            'GT',
            'GG',
            'GN',
            'GW',
            'GY',
            'HT',
            'HN',
            'HK',
            'HU',
            'IS',
            'IN',
            'ID',
            'IR',
            'IQ',
            'IE',
            'IM',
            'IL',
            'IT',
            'JM',
            'JP',
            'JE',
            'JO',
            'KZ',
            'KE',
            'KI',
            'KW',
            'KG',
            'LA',
            'LV',
            'LB',
            'LS',
            'LR',
            'LY',
            'LI',
            'LT',
            'LU',
            'MO',
            'MG',
            'MW',
            'MY',
            'MV',
            'ML',
            'MT',
            'MH',
            'MQ',
            'MR',
            'MU',
            'YT',
            'MX',
            'FM',
            'MD',
            'MC',
            'MN',
            'ME',
            'MS',
            'MA',
            'MZ',
            'MM',
            'NA',
            'NR',
            'NP',
            'NL',
            'NC',
            'NZ',
            'NI',
            'NE',
            'NG',
            'NU',
            'NF',
            'KP',
            'MK',
            'MP',
            'NO',
            'OM',
            'PK',
            'PW',
            'PS',
            'PA',
            'PG',
            'PY',
            'PE',
            'PH',
            'PL',
            'PT',
            'PR',
            'QA',
            'RE',
            'RO',
            'RU',
            'RW',
            'WS',
            'SM',
            'ST',
            'SA',
            'SN',
            'RS',
            'SC',
            'SL',
            'SG',
            'SX',
            'SK',
            'SI',
            'SB',
            'SO',
            'ZA',
            'KR',
            'SS',
            'ES',
            'LK',
            'BL',
            'SH',
            'KN',
            'LC',
            'MF',
            'PM',
            'VC',
            'SD',
            'SR',
            'SJ',
            'SE',
            'CH',
            'SY',
            'TW',
            'TJ',
            'TZ',
            'TH',
            'TL',
            'TG',
            'TK',
            'TO',
            'TT',
            'TN',
            'TR',
            'TM',
            'TC',
            'TV',
            'VI',
            'UG',
            'UA',
            'AE',
            'GB',
            'US',
            'UY',
            'UZ',
            'VU',
            'VA',
            'VE',
            'VN',
            'WF',
            'EH',
            'YE',
            'ZM',
            'ZW',
          ];

    for (const iso of isoCodes) {
      const countryObj = {
        name: countryNames[iso],
        placeholder: getExampleNumber(iso, examples)?.nationalNumber || '',
        callingCode: getCountryCallingCode(iso),
        iso: iso,
      };

      this.countries.push(countryObj);
    }

    const countryCode = this.countryCode || this.countries[0].iso;
    this.currentCountry = this.countries.find(
      (country) => country.iso === countryCode
    );
  }

  handleOpen() {
    this.open = true;
    this.renderRoot.querySelectorAll('.wf_country button')[0]?.focus();
  }

  handleClose() {
    this.open = false;
    this.input?.focus();
  }

  handleClickOutside() {
    document.addEventListener('click', (e) => {
      if (this.open && !this.renderRoot.contains(e.target)) {
        this.handleClose();
      }
    });
  }

  handleInput(e) {
    if ('insertReplacementText' === e.inputType || !('data' in e)) {
      this.hasInteracted = true;
    }
    const value = this.input.value;

    this.value = value;
    this.intPhone = this.value;
    this.emitEvent('input', { detail: this.value });
  }

  handleSwitchCountry(country, handleValidity = true) {
    this.currentCountry = country;

    this.updateComplete.then(() => {
      if (this.input) {
        this.countryWidth = this.countrySelectRef.clientWidth;

        this.style.setProperty('--country-width', `${this.countryWidth}px`);
      }

      this.minLength = this.currentCountry.placeholder.length - 2;
      this.placeholder = this.currentCountry.placeholder;
      this.maxLength = 12;

      this.countryWidth = this.countrySelectRef.value
        ? this.countrySelectRef.value.clientWidth + 10
        : 0;

      this.open = false;

      if (handleValidity) {
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  handleEscape(e) {
    if (!this.open) return;

    if (e.code === 'Escape' || e.code === 'Esc') {
      this.handleClose();
    }
  }

  handleKeyDown(e) {
    const focusEls = this.renderRoot.querySelectorAll('.wf_country button');
    const firstFocusableEl = focusEls[0];
    const lastFocusableEl = focusEls[focusEls.length - 1];
    const currentIndex = Array.from(focusEls).indexOf(document.activeElement);

    switch (e.code) {
      case 'Tab':
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableEl) {
            lastFocusableEl.focus();
            e.preventDefault();
          }
        } else if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
        break;

      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        if (currentIndex === focusEls.length - 1 || currentIndex === -1) {
          // If at last element or no element focused, go to first
          firstFocusableEl.focus();
        } else {
          // Focus next element
          focusEls[currentIndex + 1].focus();
        }
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        if (currentIndex === 0 || currentIndex === -1) {
          // If at first element or no element focused, go to last
          lastFocusableEl.focus();
        } else {
          // Focus previous element
          focusEls[currentIndex - 1].focus();
        }
        break;

      case 'Home':
        e.preventDefault();
        firstFocusableEl.focus();
        break;

      case 'End':
        e.preventDefault();
        lastFocusableEl.focus();
        break;

      case 'Enter':
      case 'Space':
        if (document.activeElement.classList.contains('country_list_button')) {
          e.preventDefault();
          document.activeElement.click();
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.handleClose();
        break;
    }
  }

  handleBlur() {
    super.handleBlur();
    this.open = false;
  }

  renderInput() {
    return html`
      <button
        type="button"
        ${ref((el) => (this.countrySelectRef = el))}
        role="combobox"
        aria-controls="country_list_box"
        aria-label=${`International Phone Selector, Current Country: ${this.currentCountry.name} +${this.currentCountry.callingCode}`}
        aria-expanded=${this.open ? 'true' : 'false'}
        @click=${this.open ? this.handleClose : this.handleOpen}
        @keydown=${this.handleEscape}
        class="country_select_button"
      >
        <img
          loading="lazy"
          src=${`https://flagcdn.com/${this.currentCountry.iso.toLowerCase()}.svg`}
          width="20"
          height="15"
          alt=${this.currentCountry.name}
          class="wf_flag ${`wf_${this.currentCountry.iso}`}"
          aria-label=${this.currentCountry.name}
        ></img >
        <span class="country_select_code">
          +${this.currentCountry.callingCode}
        </span>
        <svg class="wf_arrow ${
          this.open ? 'wf_arrow_up' : ''
        }" width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.30385 9L0.803848 0H11.8038L6.30385 9Z" fill="currentColor"/>
        </svg>
      </button>

  <ul
    class="country_list ${this.open ? ' is_open' : ''}"
    id="country_list_box"
    @keydown=${this.handleKeyDown}
  >
  ${map(
    this.countries,
    (country) => html`
      <li class="wf_country">
        <button
          type="button"
          aria-label=${`${country.name} +${country.callingCode}`}
          class="country_list_button"
          @click=${() => this.handleSwitchCountry(country)}
        >
          <img
            loading="lazy"
            src=${`https://flagcdn.com/${country.iso.toLowerCase()}.svg`}
            width="20"
            height="15"
            alt=${country.name}
            class="wf_flag ${`wf_${country.iso}`}"
            aria-label=${country.name}
          />
          <span class="country_name">${country.name}</span>
          <span class="country_code">+${country.callingCode}</span>
        </button>
      </li>
    `
  )}
      </ul>
      <input type="hidden" id=${`${ifDefined(
        this.id
      )}_hidden`} name="${ifDefined(this.name)}" value=${this.intPhone || ''} />
      <input
        type="tel"
        ${ref((el) => (this.input = el))}
        class="phone_input_field"
        id=${this.id}
        pattern=${ifDefined(this.pattern)}
        placeholder=${ifDefined(this.placeholder)}
        .value=${live(this.value)}
        ?readonly=${this.readOnly}
        ?disabled=${this.disabled}
        ?required=${this.required}
        ?autofocus=${this.autofocus}
        tabindex=${this.tabIndex}
        autocomplete=${ifDefined(this.autocomplete)}
        inputmode=${ifDefined(this.inputmode)}
        min=${ifDefined(this.validateOnly ? undefined : this.min)}
        max=${ifDefined(this.validateOnly ? undefined : this.max)}
        minlength=${ifDefined(this.minLength)}
        maxlength=${ifDefined(this.validateOnly ? undefined : this.maxLength)}
        step=${ifDefined(this.step)}
        aria-invalid=${this.invalid ? 'true' : 'false'}
        @change=${this.handleChange}
        @input=${this.handleInput}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      />
  `;
  }
}

window.customElements.get('float-phone-field') ||
  window.customElements.define('float-phone-field', FloatPhoneField);
